import { stringify } from "query-string";
import { fetchUtils } from "ra-core";

const GET_MANY_BATCH_SIZE = 100;

const chunkArray = (values, chunkSize) => {
  const chunks = [];
  for (let i = 0; i < values.length; i += chunkSize) {
    chunks.push(values.slice(i, i + chunkSize));
  }
  return chunks;
};

/**
 * Maps react-admin queries to a json-server powered REST API
 *
 * @see https://github.com/typicode/json-server
 *
 * @example
 *
 * getList          => GET http://my.api.url/posts?_sort=title&_order=ASC&_start=0&_end=24
 * getOne           => GET http://my.api.url/posts/123
 * getManyReference => GET http://my.api.url/posts?author_id=345
 * getMany          => GET http://my.api.url/posts?id=123&id=456&id=789
 * create           => POST http://my.api.url/posts/123
 * update           => PUT http://my.api.url/posts/123
 * updateMany       => PUT http://my.api.url/posts/123, PUT http://my.api.url/posts/456, PUT http://my.api.url/posts/789
 * delete           => DELETE http://my.api.url/posts/123
 *
 * @example
 *
 * import * as React from "react";
 * import { Admin, Resource } from 'react-admin';
 * import jsonServerProvider from 'ra-data-json-server';
 *
 * import { PostList } from './posts';
 *
 * const App = () => (
 *     <Admin dataProvider={jsonServerProvider('http://jsonplaceholder.typicode.com')}>
 *         <Resource name="posts" list={PostList} />
 *     </Admin>
 * );
 *
 * export default App;
 */
const dataProvider = (
  apiBaseUrl,
  apiUrl,
  httpClient = fetchUtils.fetchJson
) => ({
  getList: (resource, params) => {
    // Handle data-export resource with admin endpoint
    if (resource === "data-exporter") {
      return httpClient(`${apiBaseUrl}/data-exporter/batch`, {
        method: "GET",
      }).then(({ json }) => {
        let data = Array.isArray(json) ? json : [];

        if (params.filter) {
          Object.keys(params.filter).forEach((key) => {
            const value = params.filter[key];
            if (value) {
              data = data.filter((item) => {
                const itemValue = item[key]?.toString().toLowerCase() || "";
                return itemValue.includes(value.toString().toLowerCase());
              });
            }
          });
        }

        if (params.sort && params.sort.field) {
          data = [...data].sort((a, b) => {
            const aVal = a[params.sort.field] || "";
            const bVal = b[params.sort.field] || "";
            const order = params.sort.order === "ASC" ? 1 : -1;
            return aVal > bVal ? order : aVal < bVal ? -order : 0;
          });
        }

        const { page, perPage } = params.pagination;
        const start = (page - 1) * perPage;
        const end = start + perPage;
        const paginatedData = data.slice(start, end);

        return {
          data: paginatedData.map((item) => ({
            ...item,
            id: item.batch_id || item.id || item._id,
          })),
          total: data.length,
        };
      });
    }

    const { page, perPage } = params.pagination;
    const { field, order } = params.sort;
    const query = {
      ...fetchUtils.flattenObject(params.filter),
      _sort: field,
      _order: order,
      _start: (page - 1) * perPage,
      _end: page * perPage,
    };

    // Check if the resource is 'users' and workspace_id is provided
    if (resource === "users" && params.filter && params.filter.workspace_id) {
      // Filter users based on the provided workspace_id
      query.workspace_guids_like = params.filter.workspace_id;
    }

    // Add sorting logic for the 'role' field in the 'users' resource
    if (resource === "users" && field === "role") {
      // Assuming 'role' is nested within 'workspace_roles'
      query._sort = `workspace_roles.${field}`;
    }

    const url = `${apiUrl}/${resource}?${stringify(query)}`;

    return httpClient(url).then(({ headers, json }) => {
      if (!headers.has("x-total-count")) {
        throw new Error(
          "The X-Total-Count header is missing in the HTTP Response. The jsonServer Data Provider expects responses for lists of resources to contain this header with the total number of results to build the pagination. If you are using CORS, did you declare X-Total-Count in the Access-Control-Expose-Headers header?"
        );
      }
      return {
        data: json.map((x) => ({ ...x, id: x._id })),
        total: parseInt(headers.get("x-total-count").split("/").pop(), 10),
      };
    });
  },

  getOne: (resource, params) => {
    // Handle data-export resource with admin endpoint
    // Use batch_id in the URL path: /data-exporter/batch/{batch_id}
    if (resource === "data-exporter") {
      // params.id should be the batch_id
      return httpClient(`${apiBaseUrl}/data-exporter/batch/${params.id}`, {
        method: "GET",
      }).then(({ json }) => {
        // The API returns an object with batch_exports array
        // Extract the first item from batch_exports array for the main record
        const batchExport = json.batch_exports && json.batch_exports.length > 0 
          ? json.batch_exports[0] 
          : json;
        
        // Include batch_exports array and root-level stats for display
        // Use batch_id as the id for React Admin
        return {
          data: { 
            ...batchExport,
            // Include the full batch_exports array
            batch_exports: json.batch_exports || [],
            // Include root-level totals for reference (these are the real totals from the API)
            total_batches: json.total_batches,
            total_users: json.total_users,
            total_channels: json.total_channels,
            total_messages: json.total_messages,
            id: batchExport.batch_id || batchExport.id || json.batch_id || params.id 
          },
        };
      });
    }

    return httpClient(`${apiUrl}/${resource}/${params.id}`).then(({ json }) => ({
      data: { ...json, id: json._id },
    }));
  },

  getMany: (resource, params) => {
    const uniqueIds = Array.from(new Set((params.ids || []).filter(Boolean)));
    if (uniqueIds.length === 0) {
      return Promise.resolve({ data: [] });
    }

    const idBatches = chunkArray(uniqueIds, GET_MANY_BATCH_SIZE);

    return (async () => {
      const batchResults = [];

      for (const batchIds of idBatches) {
        const query = { id: batchIds };
        const url = `${apiUrl}/${resource}?${stringify(query)}`;
        const { json } = await httpClient(url);
        batchResults.push(
          ...json.map((x) => ({ ...x, id: x._id || x.id }))
        );
      }

      const recordsById = new Map(batchResults.map((record) => [record.id, record]));
      return {
        data: uniqueIds.map((id) => recordsById.get(id)).filter(Boolean),
      };
    })();
  },

  getManyReference: (resource, params) => {
    const { page, perPage } = params.pagination;
    const { field, order } = params.sort;
    const query = {
      ...fetchUtils.flattenObject(params.filter),
      [params.target]: params.id,
      _sort: field,
      _order: order,
      _start: (page - 1) * perPage,
      _end: page * perPage,
    };
    const url = `${apiUrl}/${resource}?${stringify(query)}`;

    return httpClient(url).then(({ headers, json }) => {
      if (!headers.has("x-total-count")) {
        throw new Error(
          "The X-Total-Count header is missing in the HTTP Response. The jsonServer Data Provider expects responses for lists of resources to contain this header with the total number of results to build the pagination. If you are using CORS, did you declare X-Total-Count in the Access-Control-Expose-Headers header?"
        );
      }
      return {
        data: json.map((x) => ({ ...x, id: x._id })),
        total: parseInt(headers.get("x-total-count").split("/").pop(), 10),
      };
    });
  },

  update: (resource, params) => {
    return httpClient(`${apiUrl}/${resource}/${params.id}`, {
      method: "PUT",
      body: JSON.stringify(params.data),
    }).then(({ json }) => ({ data: json }));
  },

  // json-server doesn't handle filters on UPDATE route, so we fallback to calling UPDATE n times instead
  updateMany: (resource, params) =>
    Promise.all(
      params.ids.map((id) =>
        httpClient(`${apiUrl}/${resource}/${id}`, {
          method: "PUT",
          body: JSON.stringify(params.data),
        })
      )
    ).then((responses) => ({ data: responses.map(({ json }) => json.id) })),

  create: (resource, params) => {
    // Handle data-export resource with admin endpoint
    if (resource === "data-exporter") {
      // Determine endpoint based on export type
      const exportType = params.exportType || "Users";
      const endpoint = exportType === "Conversations" 
        ? `${apiBaseUrl}/data-exporter/conversations`
        : `${apiBaseUrl}/data-exporter/users`;
      
      return httpClient(endpoint, {
        method: "POST",
        body: JSON.stringify(params.data || {}),
      }).then(({ json }) => ({
        data: { ...json, id: json.batch_id || json.id || json._id },
      })).catch((error) => {
        // Re-throw the error so it can be handled in the component
        throw error;
      });
    }

    return httpClient(`${apiUrl}/${resource}`, {
      method: "POST",
      body: JSON.stringify(params.data),
    }).then(({ json }) => ({
      data: { ...params.data, id: json.id },
    }));
  },

  recreateMP3: (params) =>
    httpClient(`${apiUrl}/message/${params.id}/createMP3`, {
      method: "GET",
    }).then(({ json }) => ({
      data: { ...params.data, id: json.id },
    } ) ),
  reprocess: (params) =>
      httpClient(`${apiUrl}/message/${params.id}/reprocess`, {
        method: "POST",
      }).then(({ json }) => ({
        data: { ...params.data, id: json.id },
      } ) ),   

  markMessageActive: (params) =>
    httpClient(`${apiUrl}/message/${params.id}/status/active`, {
      method: "PUT",
    }).then(({ json }) => ({
      data: json,
    })),

  markMessageDeleted: (params) =>
    httpClient(`${apiUrl}/message/${params.id}/status/deleted`, {
      method: "PUT",
    }).then(({ json }) => ({
      data: json,
    })),

  touchMessage: (params) =>
    httpClient(`${apiBaseUrl}/admin/messages/${params.id}/touch`, {
      method: "POST",
    }).then(({ json }) => ({
      data: json,
    })),

  recreateWaveform: (params) =>
    httpClient(`${apiUrl}/message/${params.id}/recreateWaveform`, {
      method: "GET",
    }).then(({ json }) => ({
      data: { ...params.data, id: json.id },
    })),

  requestAudioPath: (params) =>
    httpClient(`${apiUrl}/message/${params.id}/requestAudioPath`, {
      method: "GET",
    }).then(({ json }) => ({
      data: { ...params.data, id: json.id },
    })),

  // PUT /channel (not the react-admin `update` route) so the API runs the full
  // channel update: data_events, membership bump, visibility-changed events.
  updateChannel: (params) => {
    const body = { channel_guid: params.id };
    if (params.name !== undefined && params.name !== null) {
      body.channel_name = params.name;
    }
    if (params.visibility !== undefined && params.visibility !== null) {
      body.visibility = params.visibility;
    }
    return httpClient(`${apiBaseUrl}/channel`, {
      method: "PUT",
      body: JSON.stringify(body),
    }).then(({ json }) => ({ data: json }));
  },

  updateChannelMemberAdmin: (params) => {
    const body = {};
    if (params.role !== undefined && params.role !== null) {
      body.role = params.role;
    }
    if (params.status !== undefined && params.status !== null) {
      body.status = params.status;
    }
    const cid = encodeURIComponent(params.channelId);
    const uid = encodeURIComponent(params.userId);
    return httpClient(
      `${apiBaseUrl}/admin/ra/channels/${cid}/members/${uid}`,
      { method: "PUT", body: JSON.stringify(body) }
    ).then(({ json }) => ({ data: json }));
  },

  getPaymentPlansForUser: (params) =>
    httpClient(`${apiBaseUrl}/admin/user/payment-plans/${params.id}`, {
      method: "GET",
    }).then(({ json }) => ({
      data: json,
    })),
  setPaymentPlan: (params) =>
    httpClient(
      `${apiBaseUrl}/payment-plan/${params.pricingTierId}/?user_id=${params.userId}&expires=${params.expiresDate}&app_store_transaction_id=${params.appStoreTransactionId}`,
      {
        method: "POST",
      }
    ).then(({ json }) => ({
      data: json,
    })),
  setWorkspacePaymentPlan: (params) =>
    httpClient(
      `${apiBaseUrl}/payment-plan/${params.pricingTierId}/?workspace_id=${params.workspaceId}&expires=${params.expiresDate}`,
      {
        method: "POST",
      }
    ).then(({ json }) => ({
      data: json,
    })),
  getAllPricingTiers: (params) =>
    httpClient(`${apiBaseUrl}/payment-plan/tiers`, {
      method: "GET",
    }).then(({ json }) => ({
      data: json,
    })),
  getPaymentPlanForWorkspace: (params) =>
    httpClient(`${apiBaseUrl}/payment-plan?workspace_id=${params.id}`, {
      method: "GET",
    }).then(({ json }) => ({
      data: json,
    })),

  createInboxNotification: (payload) =>
    httpClient(`${apiBaseUrl}/inbox-notifications`, {
      method: "POST",
      body: JSON.stringify(payload),
    }).then(({ json }) => ({ data: json })),

  delete: (resource, params) => {
    return httpClient(`${apiUrl}/${resource}/${params.id}`, {
      method: "DELETE",
    }).then(({ json }) => ({ data: json }));
  },

  // json-server doesn't handle filters on DELETE route, so we fallback to calling DELETE n times instead
  deleteMany: (resource, params) =>
    Promise.all(
      params.ids.map((id) =>
        httpClient(`${apiUrl}/${resource}/${id}`, {
          method: "DELETE",
        })
      )
    ).then((responses) => ({ data: responses.map(({ json }) => json.id) })),
});

export default dataProvider;
